/**
 * LastMinuteBookingFlow — minimal 3-step "see it, click it, book it" flow.
 *
 * Steps:
 *  1. Pick a treatment (filtered to the duration bucket the user already chose)
 *  2. Customer info — name / email / phone (pre-filled if returning client)
 *  3. Confirmation
 *
 * If the slot was claimed by someone else between picking and confirming,
 * the backend returns 409 with `alternatives` — we show a graceful
 * "Just taken! Here are 3 similar openings →" panel that lets them re-book
 * in one tap without going back to the modal.
 */
import { useEffect, useMemo, useState } from "react";
import LmbServicePicker from "./lmc/LmbServicePicker";
import axios from "axios";
import { Loader2, MapPin, Clock, Calendar, Check, ChevronLeft, X, Sparkles, Crown, AlertCircle, ChevronRight, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";
import { errMsg } from "@/utils/errMsg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// localStorage key for the returning-client recognition
const RETURN_KEY = "klc:last_minute_customer";

function formatTimeLabel(start_time) {
  try {
    const [hh, mm] = start_time.split(":");
    const h = parseInt(hh, 10);
    const ampm = h < 12 ? "AM" : "PM";
    return `${h % 12 || 12}:${mm} ${ampm}`;
  } catch { return start_time; }
}

function readReturningCustomer() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(RETURN_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveReturningCustomer(c) {
  try {
    window.localStorage.setItem(RETURN_KEY, JSON.stringify({
      first_name: c.first_name,
      last_name: c.last_name,
      email: c.email,
      phone: c.phone,
      visit_count: (c.visit_count || 0) + 1,
      last_seen: new Date().toISOString(),
    }));
  } catch { /* noop */ }
}

export default function LastMinuteBookingFlow({ open, onClose, slot, onPickAlternative }) {
  const { language } = useLanguage();
  const isFr = language === "fr";

  const [step, setStep] = useState(1);            // 1=service, 2=info, 3=done
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  // Hold timer state — backend reserves the slot for 4 minutes when the
  // booking flow opens, so two visitors can't race on the same time.
  const [holdToken, setHoldToken] = useState(null);
  const [holdSecondsLeft, setHoldSecondsLeft] = useState(null);
  const [holdExpired, setHoldExpired] = useState(false);

  const returning = useMemo(readReturningCustomer, [open]);
  const [info, setInfo] = useState({ first_name: "", last_name: "", email: "", phone: "" });
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [swapPrompt, setSwapPrompt] = useState(null); // {would_replace:[], other_appointments:[]}
  const [error, setError] = useState(null);
  const [alternatives, setAlternatives] = useState(null);

  // Reset when modal opens with a new slot
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setSelectedService(null);
    setBookingResult(null);
    setError(null);
    setAlternatives(null);
    setHoldExpired(false);
    if (returning) {
      setInfo({
        first_name: returning.first_name || "",
        last_name: returning.last_name || "",
        email: returning.email || "",
        phone: returning.phone || "",
      });
    } else {
      setInfo({ first_name: "", last_name: "", email: "", phone: "" });
    }
  }, [open, slot?.location_id, slot?.date, slot?.start_time, returning]);

  // Acquire a 4-minute hold the moment we open with a fresh slot so other
  // visitors can't grab it while this client picks a service + types info.
  useEffect(() => {
    if (!open || !slot?.location_id || !slot?.start_time) return undefined;
    let cancelled = false;
    setHoldToken(null);
    setHoldSecondsLeft(null);
    setHoldExpired(false);
    axios
      .post(`${API}/last-minute/hold`, {
        location_id: slot.location_id,
        date: slot.date,
        start_time: slot.start_time,
        duration_minutes: slot.duration_minutes,
      }, { timeout: 10000 })
      .then((r) => {
        if (cancelled) return;
        setHoldToken(r.data?.hold_token || null);
        setHoldSecondsLeft(r.data?.expires_in_seconds || 240);
      })
      .catch(() => { /* non-fatal — booking endpoint still race-safe via re-check */ });
    return () => { cancelled = true; };
  }, [open, slot?.location_id, slot?.date, slot?.start_time, slot?.duration_minutes]);

  // Countdown — ticks every second once we have a hold.
  useEffect(() => {
    if (!open || holdSecondsLeft === null || holdSecondsLeft <= 0) return undefined;
    const t = setInterval(() => {
      setHoldSecondsLeft((s) => {
        if (s === null) return s;
        if (s <= 1) {
          setHoldExpired(true);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [open, holdSecondsLeft !== null]);

  // Release the hold if the user closes the modal without booking.
  const releaseAndClose = async () => {
    if (slot?.location_id && holdToken && !bookingResult) {
      try {
        await axios.post(`${API}/last-minute/release`, {
          location_id: slot.location_id,
          date: slot.date,
          start_time: slot.start_time,
          duration_minutes: slot.duration_minutes,
        }, { timeout: 5000 });
      } catch { /* noop */ }
    }
    onClose?.();
  };

  // Fetch + filter catalog services by duration when the modal opens.
  // When the slot is Botox-only (a 20-29 min "tight gap" emitted by the
  // gap calculator), narrow the catalog to Botox services only so the
  // client can't accidentally pick a treatment that won't fit and get
  // rejected by the server-side guard.
  useEffect(() => {
    if (!open || !slot?.location_id || !slot?.duration_minutes) return undefined;
    let cancelled = false;
    setLoadingServices(true);
    axios
      .get(`${API}/square/catalog-services/${slot.location_id}`, { timeout: 25000 })
      .then((res) => {
        if (cancelled) return;
        const all = res.data?.services || [];
        // Anything that FITS the gap (≤ slot length) — Square books the service's own duration.
        let filtered = all.filter(
          (s) => Number(s.duration_minutes) > 0 && Number(s.duration_minutes) <= Number(slot.duration_minutes)
        );
        if (slot.botox_only) {
          // Botox-only tight-gap slot — keep only services whose name reads
          // like Botox (covers "Botox Touch Up", "Tox Refresh", "Botox Lip
          // Flip", etc.). Brand-agnostic safety: also accept "tox" as a
          // standalone keyword.
          filtered = filtered.filter((s) => {
            const n = (s.name || "").toLowerCase();
            return n.includes("botox") || /\btox\b/.test(n) || n.includes("tox refresh") || n.includes("touch-up") || n.includes("touch up");
          });
        }
        setServices(filtered);
        // Auto-pick if only one match — saves the client an extra tap when
        // the LMC pre-narrowed their options to a single Botox variation.
        if (filtered.length === 1) setSelectedService(filtered[0]);
      })
      .catch(() => { if (!cancelled) setServices([]); })
      .finally(() => { if (!cancelled) setLoadingServices(false); });
    return () => { cancelled = true; };
  }, [open, slot?.location_id, slot?.duration_minutes, slot?.botox_only]);

  if (!slot) return null;

  // ── Per-field validation ────────────────────────────────────────────
  // Runs on blur so users see problems the moment they leave a field,
  // instead of finding out at "Confirm & Book". Matches the backend
  // sanitiser: phone must have 10-15 digits, email must be well-formed,
  // last name can't be a note dropped into the wrong slot.
  const validateField = (key, val) => {
    const isFrLang = isFr;
    const raw = (val ?? "").trim();
    if (key === "first_name" || key === "last_name") {
      if (!raw) return isFrLang ? "Requis." : "Required.";
      if (raw.length > 60) return isFrLang ? "Trop long — utilisez votre vrai nom." : "Too long — please use your real name.";
      if (/[.@!?]|http|www\.|\d{4,}/i.test(raw)) return isFrLang ? "Ce champ semble contenir une note — utilisez votre nom." : "This looks like a note — please enter your name only.";
      if (raw.split(/\s+/).length > 3) return isFrLang ? "Utilisez votre prénom / nom uniquement." : "Please use just your first or last name.";
      return "";
    }
    if (key === "email") {
      if (!raw) return isFrLang ? "Requis." : "Required.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) return isFrLang ? "Courriel invalide." : "Please enter a valid email.";
      return "";
    }
    if (key === "phone") {
      if (!raw) return isFrLang ? "Requis." : "Required.";
      const digits = raw.replace(/\D/g, "");
      if (digits.length < 10) return isFrLang ? "Numéro de téléphone incomplet (10 chiffres min)." : "Please enter a valid phone number (10 digits minimum).";
      if (digits.length > 15) return isFrLang ? "Numéro trop long." : "Phone number is too long.";
      // Catch the classic "I'm not sure what I need" note-in-phone case
      if (/[a-zA-Z]{3,}/.test(raw)) return isFrLang ? "Le téléphone ne doit contenir que des chiffres." : "Phone should only contain digits — please leave any questions for the last step.";
      return "";
    }
    return "";
  };

  const handleFieldBlur = (key) => {
    const err = validateField(key, info[key]);
    setFieldErrors((prev) => ({ ...prev, [key]: err }));
  };

  const handleFieldChange = (key, val) => {
    setInfo((p) => ({ ...p, [key]: val }));
    // Clear the error as the user starts typing again — feels responsive.
    if (fieldErrors[key]) {
      setFieldErrors((prev) => ({ ...prev, [key]: "" }));
    }
  };

  const submit = async (confirmReplace = false) => {
    setError(null);
    setSwapPrompt(null);
    if (!selectedService) { setError(isFr ? "Choisissez un soin." : "Pick a treatment."); return; }
    // Validate every field one final time before firing the request.
    const errs = {};
    for (const k of ["first_name", "last_name", "email", "phone"]) {
      const e = validateField(k, info[k]);
      if (e) errs[k] = e;
    }
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setError(isFr ? "Corrigez les champs mis en évidence." : "Please fix the highlighted fields.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        location_id: slot.location_id,
        date: slot.date,
        start_time: slot.start_time,
        duration_minutes: slot.duration_minutes,
        service_variation_id: selectedService.variation_id,
        service_name: selectedService.name,
        hold_token: holdToken || undefined,
        // Iter 443 · Sep 21 2026 · pass the provider through so Tanya's
        // LMC picks land on her Square calendar (TM22D3lg4YNVms4n),
        // not Karine's. Missing → backend defaults to Karine.
        provider: slot.provider || undefined,
        confirm_replace: confirmReplace === true,
        customer: {
          first_name: info.first_name.trim(),
          last_name: info.last_name.trim(),
          email: info.email.trim(),
          phone: info.phone.trim(),
        },
      };
      const res = await axios.post(`${API}/last-minute/book`, payload, { timeout: 45000 });
      saveReturningCustomer(info);
      setBookingResult(res.data);
      setStep(3);
    } catch (e) {
      const data = errMsg(e);
      // Manual-review path (NEW) — booking attempt was escalated to staff.
      // Show a friendly "we're on it" confirmation and clear the form so
      // the client doesn't think the system is broken.
      if (e?.response?.status === 409 && data?.code === "manual_review") {
        setBookingResult({
          manual_review: true,
          review_id: data.review_id,
          message: data.message
            || "We've flagged this for Karine — she'll text you within minutes.",
        });
        setStep(3);
        return;
      }
      // Oct 2026 — this earlier slot would replace a later appointment:
      // ask before we cancel anything.
      if (e?.response?.status === 409 && data?.code === "needs_swap_confirmation") {
        setSwapPrompt({ would_replace: data.would_replace || [], other_appointments: data.other_appointments || [] });
        return;
      }
      // 409 with structured alternatives → graceful "just taken" path
      if (e?.response?.status === 409 && data?.code === "slot_taken") {
        setAlternatives(data.alternatives || []);
      } else if (typeof data === "string") {
        setError(data);
      } else {
        setError(isFr ? "Impossible de réserver — réessayez." : "Could not book — try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && releaseAndClose()}>
      <DialogContent
        className="max-w-md bg-gradient-to-br from-zinc-950 via-zinc-900 to-amber-950/20 border-amber-500/30 text-zinc-100 p-0 max-h-[92vh] overflow-y-auto"
        data-testid="last-minute-booking-flow"
      >
        {/* Slot summary header — always visible so user never doubts what they're booking */}
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-amber-500/20">
          <DialogTitle className="text-amber-200 font-serif text-lg tracking-wide flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-amber-400" />
            {isFr ? "Réservation rapide" : "Quick Booking"}
          </DialogTitle>
          <div className="mt-2 grid grid-cols-1 gap-1.5 text-xs text-zinc-300">
            {/* Iter 457 · Feb 2026 · Karine ask — explicit provider chip so
                the client can never mistake a Tanya (Medical Aesthetician)
                slot for a Karine (Nurse Injector) slot. Amber for Karine,
                sky-blue for Tanya. Only shows when the slot carries a
                provider tag (LMC-originated flows). */}
            {slot.provider && (
              <span
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border w-fit font-semibold ${
                  slot.provider === "tanya"
                    ? "bg-sky-500/10 border-sky-500/40 text-sky-200"
                    : "bg-amber-500/10 border-amber-500/40 text-amber-200"
                }`}
                data-testid={`lmb-provider-badge-${slot.provider}`}
              >
                <User className="w-3 h-3" />
                {slot.provider === "tanya"
                  ? (isFr ? "Avec Tanya · esthéticienne médicale" : "With Tanya · Medical Aesthetician")
                  : (isFr ? "Avec Karine · infirmière injectrice" : "With Karine · Nurse Injector")}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <MapPin className="w-3 h-3 text-zinc-500" />
              {slot.location_name}
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar className="w-3 h-3 text-zinc-500" />
              {slot.weekday ? `${slot.weekday} · ${slot.month_day}` : slot.date}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-3 h-3 text-zinc-500" />
              {formatTimeLabel(slot.start_time)} · {slot.duration_minutes} min
            </span>
          </div>
          {/* Hold countdown — only when active and not on success step */}
          {step !== 3 && !alternatives && holdSecondsLeft !== null && !holdExpired && (
            <div
              className="mt-3 px-2.5 py-1.5 rounded-md bg-amber-500/10 border border-amber-500/30 flex items-center gap-2 text-[11px] text-amber-200"
              data-testid="lmb-hold-countdown"
            >
              <Crown className="w-3 h-3 text-amber-400" />
              {isFr ? "Plage réservée pour " : "Slot held for "}
              <span className="font-mono font-semibold tabular-nums">
                {Math.floor(holdSecondsLeft / 60)}:{String(holdSecondsLeft % 60).padStart(2, "0")}
              </span>
            </div>
          )}
          {holdExpired && step !== 3 && !alternatives && (
            <div
              className="mt-3 px-2.5 py-1.5 rounded-md bg-rose-500/10 border border-rose-500/30 text-[11px] text-rose-300"
              data-testid="lmb-hold-expired"
            >
              {isFr
                ? "Votre réservation temporaire a expiré. Confirmez vite — un autre client pourrait la prendre."
                : "Your temporary hold expired. Confirm quickly — another client may grab it."}
            </div>
          )}
        </DialogHeader>

        {/* Alternatives panel — overrides everything when slot was claimed */}
        {swapPrompt ? (
          <div className="px-5 py-5 space-y-3" data-testid="lmb-swap-prompt">
            <h3 className="text-amber-200 font-serif text-base">
              {isFr ? "Remplacer votre rendez-vous existant ?" : "Replace your existing appointment?"}
            </h3>
            <p className="text-zinc-300 text-sm">
              {isFr
                ? "Vous avez déjà un rendez-vous plus tard. Si vous prenez cette place, nous annulerons celui-ci pour que vous n'en ayez pas deux :"
                : "You already have a later appointment. Booking this earlier slot will release it so you don't hold two:"}
            </p>
            <ul className="space-y-1.5">
              {swapPrompt.would_replace.map((a) => (
                <li key={a.booking_id} className="rounded-lg bg-rose-500/10 border border-rose-500/40 px-3 py-2 text-sm text-rose-200" data-testid={`lmb-swap-item-${a.booking_id}`}>
                  <span className="font-semibold">{a.service}</span> · {a.date} {a.time} · {a.location}
                </li>
              ))}
            </ul>
            {swapPrompt.other_appointments.length > 0 && (
              <p className="text-zinc-500 text-[11px]">
                {isFr ? "Vos autres rendez-vous ne changent pas." : "Your other appointments stay as they are."}
              </p>
            )}
            <div className="grid grid-cols-2 gap-2 pt-1">
              <Button variant="outline" onClick={() => setSwapPrompt(null)} className="border-zinc-600 text-zinc-200 bg-transparent hover:bg-zinc-800" data-testid="lmb-swap-keep">
                {isFr ? "Garder l'original" : "Keep my original"}
              </Button>
              <Button onClick={() => submit(true)} disabled={submitting} className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold" data-testid="lmb-swap-confirm">
                {submitting ? (isFr ? "Réservation…" : "Booking…") : (isFr ? "Oui, déplacer plus tôt" : "Yes, move it earlier")}
              </Button>
            </div>
          </div>
        ) : alternatives ? (
          <div className="p-5 space-y-3" data-testid="lmb-alternatives">
            <div className="flex items-center gap-2 text-amber-300">
              <AlertCircle className="w-4 h-4" />
              <p className="text-sm font-medium">
                {isFr
                  ? "Cette plage vient d'être prise — voici 3 alternatives :"
                  : "That slot was just claimed — here are 3 alternatives:"}
              </p>
            </div>
            {alternatives.length === 0 ? (
              <p className="text-zinc-500 text-xs">
                {isFr ? "Aucune autre ouverture pour le moment." : "No other openings right now."}
              </p>
            ) : (
              <div className="space-y-2">
                {alternatives.map((alt) => (
                  <button
                    key={`${alt.location_id}-${alt.date}-${alt.start_time}`}
                    onClick={() => {
                      setAlternatives(null);
                      onPickAlternative?.(alt);
                    }}
                    className="w-full px-3 py-3 rounded-lg border border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/15 text-left flex items-center justify-between gap-3 transition"
                    data-testid={`lmb-alt-${alt.location_id}-${alt.date}-${alt.start_time}`}
                  >
                    <div className="text-amber-200 text-sm">
                      <div className="font-semibold">{alt.weekday}, {alt.month_day} · {alt.label}</div>
                      <div className="text-[11px] text-zinc-400">{alt.location_name}</div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-amber-400" />
                  </button>
                ))}
              </div>
            )}
            <Button variant="ghost" onClick={releaseAndClose} className="w-full text-zinc-400 hover:text-zinc-100">
              {isFr ? "Fermer" : "Close"}
            </Button>
          </div>
        ) : step === 1 ? (
          <div className="p-5 space-y-3">
            <div>
              <p className="text-[10px] text-zinc-500 uppercase tracking-widest mb-1">
                {isFr ? "Quel soin ?" : "Which treatment?"}
              </p>
              {returning?.first_name && (
                <p className="text-emerald-300 text-xs mb-2" data-testid="lmb-welcome-back">
                  {isFr ? `Bon retour, ${returning.first_name} ✨` : `Welcome back, ${returning.first_name} ✨`}
                </p>
              )}
            </div>
            {slot?.botox_only && (
              <div
                className="rounded-xl border border-rose-400/40 bg-rose-500/10 px-3 py-2.5 flex items-start gap-2"
                data-testid="lmb-botox-only-banner"
                role="alert"
              >
                <span className="text-rose-200 text-base leading-none mt-0.5">💉</span>
                <div className="text-rose-100 text-[12.5px] leading-snug">
                  <div className="font-semibold mb-0.5">
                    {isFr ? "Créneau express · Botox uniquement" : "Express slot · Botox only"}
                  </div>
                  <div className="text-rose-100/85">
                    {isFr
                      ? `Ce créneau (${slot.tight_gap_minutes || 20} min) a seulement assez de temps pour un traitement Botox. Veuillez choisir une option Botox ci-dessous.`
                      : `This selected time only has enough time for a Botox treatment (${slot.tight_gap_minutes || 20} min gap). Please choose a Botox option below.`}
                  </div>
                </div>
              </div>
            )}
            {loadingServices ? (
              <div className="flex items-center justify-center py-8 text-zinc-500">
                <Loader2 className="w-4 h-4 animate-spin mr-2 text-amber-400" /> Loading…
              </div>
            ) : services.length === 0 ? (
              <p className="text-zinc-500 text-xs text-center py-6">
                {slot?.botox_only
                  ? (isFr
                      ? "Aucun soin Botox de cette durée à cette clinique. Choisissez un autre créneau."
                      : "No Botox service of this length is set up at this clinic in Square. Pick another opening.")
                  : (isFr
                      ? "Aucun soin de cette durée à cette clinique."
                      : "No treatments of this length at this clinic.")}
              </p>
            ) : (
              <LmbServicePicker services={services} selected={selectedService} onPick={setSelectedService} isFr={isFr} />
            )}
            <Button
              onClick={() => setStep(2)}
              disabled={!selectedService}
              className="w-full bg-amber-600 hover:bg-amber-500 text-white"
              data-testid="lmb-continue-to-info"
            >
              {isFr ? "Continuer" : "Continue"}
            </Button>
          </div>
        ) : step === 2 ? (
          <div className="p-5 space-y-3">
            <button
              onClick={() => setStep(1)}
              className="text-zinc-400 hover:text-zinc-100 text-xs flex items-center gap-1"
              data-testid="lmb-back-to-service"
            >
              <ChevronLeft className="w-3.5 h-3.5" /> {isFr ? "Retour" : "Back"}
            </button>
            <p className="text-[10px] text-zinc-500 uppercase tracking-widest">
              {isFr ? "Vos coordonnées" : "Your details"}
            </p>
            {returning?.first_name && (
              <p className="text-emerald-300 text-[11px] -mt-2" data-testid="lmb-prefill-hint">
                {isFr ? "Pré-rempli — vérifiez et confirmez." : "Pre-filled — review and confirm."}
              </p>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px] text-zinc-500">{isFr ? "Prénom" : "First name"}</Label>
                <Input
                  value={info.first_name}
                  onChange={(e) => handleFieldChange("first_name", e.target.value)}
                  onBlur={() => handleFieldBlur("first_name")}
                  className={`bg-zinc-900 text-zinc-100 ${fieldErrors.first_name ? "border-rose-500" : "border-zinc-700"}`}
                  data-testid="lmb-first-name"
                />
                {fieldErrors.first_name && (
                  <p className="text-rose-400 text-[10px] mt-1" data-testid="lmb-first-name-err">{fieldErrors.first_name}</p>
                )}
              </div>
              <div>
                <Label className="text-[10px] text-zinc-500">{isFr ? "Nom" : "Last name"}</Label>
                <Input
                  value={info.last_name}
                  onChange={(e) => handleFieldChange("last_name", e.target.value)}
                  onBlur={() => handleFieldBlur("last_name")}
                  className={`bg-zinc-900 text-zinc-100 ${fieldErrors.last_name ? "border-rose-500" : "border-zinc-700"}`}
                  data-testid="lmb-last-name"
                />
                {fieldErrors.last_name && (
                  <p className="text-rose-400 text-[10px] mt-1" data-testid="lmb-last-name-err">{fieldErrors.last_name}</p>
                )}
              </div>
            </div>
            <div>
              <Label className="text-[10px] text-zinc-500">{isFr ? "Courriel" : "Email"}</Label>
              <Input
                type="email"
                value={info.email}
                onChange={(e) => handleFieldChange("email", e.target.value)}
                onBlur={() => handleFieldBlur("email")}
                className={`bg-zinc-900 text-zinc-100 ${fieldErrors.email ? "border-rose-500" : "border-zinc-700"}`}
                data-testid="lmb-email"
              />
              {fieldErrors.email && (
                <p className="text-rose-400 text-[10px] mt-1" data-testid="lmb-email-err">{fieldErrors.email}</p>
              )}
            </div>
            <div>
              <Label className="text-[10px] text-zinc-500">{isFr ? "Téléphone" : "Phone"}</Label>
              <Input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                placeholder="613-555-0100"
                value={info.phone}
                onChange={(e) => handleFieldChange("phone", e.target.value)}
                onBlur={() => handleFieldBlur("phone")}
                className={`bg-zinc-900 text-zinc-100 ${fieldErrors.phone ? "border-rose-500" : "border-zinc-700"}`}
                data-testid="lmb-phone"
              />
              {fieldErrors.phone && (
                <p className="text-rose-400 text-[10px] mt-1" data-testid="lmb-phone-err">{fieldErrors.phone}</p>
              )}
            </div>
            {error && <p className="text-rose-300 text-xs" data-testid="lmb-error">{error}</p>}
            <Button
              onClick={() => submit(false)}
              disabled={submitting}
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white font-semibold"
              data-testid="lmb-confirm-book"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> {isFr ? "Réservation…" : "Booking…"}</>
              ) : (
                <>{isFr ? "Confirmer et réserver" : "Confirm & Book"}</>
              )}
            </Button>
            <p className="text-[10px] text-zinc-600 text-center">
              {isFr
                ? "Aucune carte requise. Confirmation envoyée par courriel."
                : "No card required. Confirmation sent by email."}
            </p>
          </div>
        ) : (
          // Step 3 — success OR manual-review pending
          bookingResult?.manual_review ? (
            <div className="p-6 text-center space-y-4" data-testid="lmb-manual-review">
              <div className="w-14 h-14 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center mx-auto">
                <Clock className="w-7 h-7 text-amber-300" />
              </div>
              <div>
                <h3 className="text-amber-200 text-lg font-serif tracking-wide">
                  {isFr ? "Karine vous textera bientôt" : "Karine will text you shortly"}
                </h3>
                <p className="text-zinc-300 text-xs mt-2 leading-relaxed">
                  {bookingResult.message}
                </p>
              </div>
              <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-3 text-left text-xs text-zinc-300 space-y-1">
                <p className="flex items-center gap-1.5"><MapPin className="w-3 h-3 text-zinc-500" /> {slot.location_name}</p>
                <p className="flex items-center gap-1.5"><Calendar className="w-3 h-3 text-zinc-500" /> {slot.weekday ? `${slot.weekday} · ${slot.month_day}` : slot.date}</p>
                <p className="flex items-center gap-1.5"><Clock className="w-3 h-3 text-zinc-500" /> {formatTimeLabel(slot.start_time)} · {slot.duration_minutes} min</p>
                <p className="flex items-center gap-1.5"><Sparkles className="w-3 h-3 text-zinc-500" /> {selectedService?.name}</p>
              </div>
              <p className="text-zinc-500 text-[11px]">
                {isFr
                  ? "Aucune carte n'a été facturée. Référence :"
                  : "No card was charged. Reference:"}{" "}
                <span className="font-mono text-zinc-400">{bookingResult.review_id?.slice(0, 8)}</span>
              </p>
              <Button onClick={onClose} className="w-full bg-zinc-800 hover:bg-zinc-700" data-testid="lmb-close-manual-review">
                {isFr ? "Fermer" : "Close"}
              </Button>
            </div>
          ) : (
          <div className="p-6 text-center space-y-4" data-testid="lmb-success">
            <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mx-auto">
              <Check className="w-7 h-7 text-emerald-300" />
            </div>
            <div>
              <h3 className="text-amber-200 text-lg font-serif tracking-wide">
                {isFr ? "Réservé !" : "Booked!"}
              </h3>
              {bookingResult?.confirmation_number && (
                <p className="text-amber-400 font-mono text-xs mt-1">
                  #{bookingResult.confirmation_number}
                </p>
              )}
            </div>
            <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-3 text-left text-xs text-zinc-300 space-y-1">
              <p className="flex items-center gap-1.5"><MapPin className="w-3 h-3 text-zinc-500" /> {slot.location_name}</p>
              <p className="flex items-center gap-1.5"><Calendar className="w-3 h-3 text-zinc-500" /> {slot.weekday ? `${slot.weekday} · ${slot.month_day}` : slot.date}</p>
              <p className="flex items-center gap-1.5"><Clock className="w-3 h-3 text-zinc-500" /> {formatTimeLabel(slot.start_time)} · {slot.duration_minutes} min</p>
              <p className="flex items-center gap-1.5"><Sparkles className="w-3 h-3 text-zinc-500" /> {selectedService?.name}</p>
            </div>
            <p className="text-zinc-500 text-[11px]">
              {isFr
                ? "Confirmation envoyée à votre courriel."
                : "Confirmation sent to your email."}
            </p>
            {bookingResult?.replaced_appointments?.length > 0 && (
              <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/40 px-3 py-2.5 text-left" data-testid="lmb-replaced-appointments">
                <p className="text-emerald-200 text-xs font-semibold">
                  {isFr ? "Votre rendez-vous plus tard a été annulé pour libérer la place :" : "Your later appointment was released so you don't hold two:"}
                </p>
                {bookingResult.replaced_appointments.map((a) => (
                  <p key={a.booking_id} className="text-emerald-300/80 text-[11px] mt-0.5">{a.service} · {a.date} {a.time}</p>
                ))}
              </div>
            )}
            {bookingResult?.points_awarded > 0 && (
              <div
                className="rounded-lg bg-amber-500/10 border border-amber-500/40 px-3 py-2.5"
                data-testid="lmb-points-awarded"
              >
                <p className="text-amber-200 text-sm font-semibold flex items-center justify-center gap-1.5">
                  <Crown className="w-4 h-4 text-amber-400" />
                  +{bookingResult.points_awarded} Crown Points
                </p>
                <p className="text-amber-300/70 text-[10px] mt-0.5">
                  {isFr ? "Bonus 2× Last Minute Club" : "2× Last Minute Club bonus"}
                </p>
              </div>
            )}
            <Button onClick={onClose} className="w-full bg-zinc-800 hover:bg-zinc-700" data-testid="lmb-close-success">
              {isFr ? "Terminé" : "Done"}
            </Button>
          </div>
          )
        )}

        {!alternatives && step !== 3 && (
          <div className="px-5 pb-3 pt-1 border-t border-zinc-800/60 flex justify-between items-center">
            <p className="text-[10px] text-zinc-600">
              {isFr ? "3 étapes · moins de 30 sec" : "3 steps · under 30 sec"}
            </p>
            <Button variant="ghost" size="sm" onClick={releaseAndClose} className="text-zinc-500 hover:text-zinc-200 h-7">
              <X className="w-3.5 h-3.5 mr-1" /> {isFr ? "Annuler" : "Cancel"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
